// computeNoticeBoard.ts — unified alert aggregation for Dashboard Notice Board
// Merges Dashboard priority actions + PropertyDetailPage alert categories into
// a single sorted NoticeBoardItem[]. Pure function, no side effects.

import type { PropertyRecord } from "../types/portfolio";
import type { Structure } from "../types/entities";
import type { AppSettings } from "../components/settings/SettingsPage";
import type { NoticeBoardItem, NoticePriority } from "../types/noticeboard";
import { annualLoanRepayment, loanPaymentEstimate } from "./calculations";

interface PortfolioMetrics {
  lvrPct: number;
  usableEquity: number;
  monthlyShortfall: number;
}

function daysUntilDate(isoDate: string): number {
  const target = new Date(isoDate);
  if (isNaN(target.getTime())) return Infinity;
  return Math.round((target.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

/** Per-property notices — used on PropertyDetailPage. No portfolio-level items. */
export function computePropertyNotices(
  p: PropertyRecord,
  appSettings: AppSettings,
): NoticeBoardItem[] {
  const items: NoticeBoardItem[] = [];
  const disabled = appSettings.disabledAlertCategories ?? [];
  const name = p.nickname || p.address || "Property";

  // IO expiry
  if ((p.ioTermYears ?? 0) > 0 && p.loanStartDate) {
    const ioEnd = new Date(p.loanStartDate);
    ioEnd.setFullYear(ioEnd.getFullYear() + (p.ioTermYears ?? 0));
    const days = daysUntilDate(ioEnd.toISOString());
    if (days <= 365) {
      const ioMonthly = Math.round(annualLoanRepayment(p).total / 12);
      const piMonthly = Math.round(loanPaymentEstimate(p.loanBalance, p.rate, p.loanTermYears || 30));
      const delta = piMonthly - ioMonthly;
      items.push({
        id: `io-expiry-${p.id}`, category: "io-expiry",
        priority: days <= 90 ? "red" : "amber",
        title: "IO period ending",
        description: `Reverts to P&I${days > 0 ? ` in ${Math.ceil(days / 30)} months` : " (overdue)"} — repayment +$${delta.toLocaleString()}/mo`,
        daysUntil: days, dueDate: ioEnd.toISOString().slice(0, 10),
        propertyId: p.id, propertyName: name, page: "loans",
      });
    }
  }

  // Fixed rate expiry
  if ((p.rateType ?? "").toLowerCase() === "fixed" && p.fixedRateExpiryDate) {
    const days = daysUntilDate(p.fixedRateExpiryDate);
    if (days <= 365) {
      items.push({
        id: `fixed-rate-expiry-${p.id}`, category: "fixed-rate-expiry",
        priority: "amber", title: "Fixed rate expiring",
        description: `Fixed rate expires ${formatDate(p.fixedRateExpiryDate)} — review your rate`,
        daysUntil: days, dueDate: p.fixedRateExpiryDate,
        propertyId: p.id, propertyName: name, page: "loans",
      });
    }
  }

  // Lease expiry
  if (p.leaseEndDate) {
    const days = daysUntilDate(p.leaseEndDate);
    if (days <= 180) {
      items.push({
        id: `lease-expiry-${p.id}`, category: "lease-expiry",
        priority: days <= 60 ? "red" : "amber", title: "Lease expiring",
        description: `Lease ends ${formatDate(p.leaseEndDate)}`,
        daysUntil: days, dueDate: p.leaseEndDate,
        propertyId: p.id, propertyName: name, page: "simulator",
      });
    }
  }

  // Insurance renewal
  for (const pol of p.insurancePolicies ?? []) {
    if (pol.reminderEnabled && pol.endDate) {
      const days = daysUntilDate(pol.endDate);
      if (days <= 60) {
        items.push({
          id: `insurance-renewal-${p.id}-${pol.id}`, category: "insurance-renewal",
          priority: days <= 14 ? "red" : "amber",
          title: `${pol.type || "Insurance"} renewal`,
          description: `${pol.type || "Policy"} expires ${formatDate(pol.endDate)}`,
          daysUntil: days, dueDate: pol.endDate,
          propertyId: p.id, propertyName: name, page: "portfolio",
        });
      }
    }
  }

  // Rent review
  if (p.rentReviewDate) {
    const days = daysUntilDate(p.rentReviewDate);
    if (days <= 90) {
      items.push({ id: `rent-review-${p.id}`, category: "rent-review", priority: days <= 14 ? "red" : "amber", title: "Rent review due", description: `Rent review ${formatDate(p.rentReviewDate)}`, daysUntil: days, dueDate: p.rentReviewDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Smoke alarm
  if (p.smokeAlarmExpiryDate) {
    const days = daysUntilDate(p.smokeAlarmExpiryDate);
    if (days <= 60) {
      items.push({ id: `smoke-alarm-${p.id}`, category: "smoke-alarm", priority: days <= 14 ? "red" : "amber", title: "Smoke alarm cert expiring", description: `Certification expires ${formatDate(p.smokeAlarmExpiryDate)}`, daysUntil: days, dueDate: p.smokeAlarmExpiryDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Pool safety
  if (p.poolSafetyCertDate) {
    const days = daysUntilDate(p.poolSafetyCertDate);
    if (days <= 60) {
      items.push({ id: `pool-safety-${p.id}`, category: "pool-safety", priority: days <= 14 ? "red" : "amber", title: "Pool safety cert expiring", description: `Certificate expires ${formatDate(p.poolSafetyCertDate)}`, daysUntil: days, dueDate: p.poolSafetyCertDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Council rates
  if (p.councilRatesDueDate) {
    const days = daysUntilDate(p.councilRatesDueDate);
    if (days <= 30) {
      items.push({ id: `council-rates-${p.id}`, category: "council-rates", priority: days <= 7 ? "red" : "amber", title: "Council rates due", description: `Due ${formatDate(p.councilRatesDueDate)}`, daysUntil: days, dueDate: p.councilRatesDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Water rates
  if (p.waterRatesDueDate) {
    const days = daysUntilDate(p.waterRatesDueDate);
    if (days <= 30) {
      items.push({ id: `water-rates-${p.id}`, category: "water-rates", priority: days <= 7 ? "red" : "amber", title: "Water rates due", description: `Due ${formatDate(p.waterRatesDueDate)}`, daysUntil: days, dueDate: p.waterRatesDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Strata levy
  if (p.strataLevyDueDate) {
    const days = daysUntilDate(p.strataLevyDueDate);
    if (days <= 30) {
      items.push({ id: `strata-levy-${p.id}`, category: "strata-levy", priority: days <= 7 ? "red" : "amber", title: "Strata levy due", description: `Due ${formatDate(p.strataLevyDueDate)}`, daysUntil: days, dueDate: p.strataLevyDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // Valuation stale (>2 years old)
  if (p.lastValuationDate) {
    const daysSince = -daysUntilDate(p.lastValuationDate); // negative because date is in the past
    if (daysSince > 730) {
      items.push({ id: `valuation-stale-${p.id}`, category: "valuation-stale", priority: "amber", title: "Bank valuation older than 2 years", description: `Last valued ${formatDate(p.lastValuationDate)} — consider a fresh valuation`, daysUntil: null, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  return enrichEmailActions(items.filter((item) => !disabled.includes(item.category)));
}

/** Maps notice categories to their email template + recipient role */
const EMAIL_ACTION_MAP: Record<string, { templateId: string; recipientRole: string }> = {
  "rent-review": { templateId: "rent-review-pm", recipientRole: "pm" },
  "lease-expiry": { templateId: "lease-expiry-pm", recipientRole: "pm" },
  "insurance-renewal": { templateId: "insurance-renewal-pm", recipientRole: "pm" },
  "smoke-alarm": { templateId: "smoke-alarm-pm", recipientRole: "pm" },
  "pool-safety": { templateId: "pool-safety-pm", recipientRole: "pm" },
  "io-expiry": { templateId: "io-expiry-broker", recipientRole: "mortgage-broker" },
  "fixed-rate-expiry": { templateId: "fixed-rate-broker", recipientRole: "mortgage-broker" },
  "valuation-stale": { templateId: "valuation-broker", recipientRole: "broker" },
  "trust-deadline": { templateId: "trust-deadline-accountant", recipientRole: "accountant" },
  "equity-available": { templateId: "equity-available-buyer-agent", recipientRole: "buyer-agent" },
};

function enrichEmailActions(items: NoticeBoardItem[]): NoticeBoardItem[] {
  return items.map((item) => {
    const action = EMAIL_ACTION_MAP[item.category];
    return action ? { ...item, emailAction: action } : item;
  });
}

function propName(p: PropertyRecord): string {
  return p.nickname || p.address || "Property";
}

function isTrustStructure(s: Structure): boolean {
  const t = (s.entityType ?? "").toLowerCase();
  return t.includes("trust") || t === "discretionary trust" || t === "unit trust" || t === "family trust";
}

const PRIORITY_ORDER: Record<NoticePriority, number> = { red: 0, amber: 1, green: 2, neutral: 3 };

export function computeNoticeBoard(
  properties: PropertyRecord[],
  structures: Structure[],
  appSettings: AppSettings,
  metrics: PortfolioMetrics,
): NoticeBoardItem[] {
  const items: NoticeBoardItem[] = [];
  const active = properties.filter((p) => p.status !== "sold");
  const disabled = appSettings.disabledAlertCategories ?? [];
  const today = new Date();

  // ── Per-property alerts ──

  for (const p of active) {
    const name = propName(p);

    // IO expiry
    if ((p.ioTermYears ?? 0) > 0 && p.loanStartDate) {
      const ioEnd = new Date(p.loanStartDate);
      ioEnd.setFullYear(ioEnd.getFullYear() + (p.ioTermYears ?? 0));
      const days = daysUntilDate(ioEnd.toISOString());
      if (days <= 365) {
        const ioMonthly = Math.round(annualLoanRepayment(p).total / 12);
        const piMonthly = Math.round(loanPaymentEstimate(p.loanBalance, p.rate, p.loanTermYears || 30));
        const delta = piMonthly - ioMonthly;
        items.push({
          id: `io-expiry-${p.id}`,
          category: "io-expiry",
          priority: days <= 90 ? "red" : "amber",
          title: "IO period ending",
          description: `${name} reverts to P&I${days > 0 ? ` in ${Math.ceil(days / 30)} months` : " (overdue)"} — repayment +$${delta.toLocaleString()}/mo`,
          daysUntil: days,
          dueDate: ioEnd.toISOString().slice(0, 10),
          propertyId: p.id,
          propertyName: name,
          page: "loans",
        });
      }
    }

    // Fixed rate expiry
    if ((p.rateType ?? "").toLowerCase() === "fixed" && p.fixedRateExpiryDate) {
      const days = daysUntilDate(p.fixedRateExpiryDate);
      if (days <= 365) {
        items.push({
          id: `fixed-rate-expiry-${p.id}`,
          category: "fixed-rate-expiry",
          priority: "amber",
          title: "Fixed rate expiring",
          description: `${name} fixed rate expires ${formatDate(p.fixedRateExpiryDate)} — review your rate`,
          daysUntil: days,
          dueDate: p.fixedRateExpiryDate,
          propertyId: p.id,
          propertyName: name,
          page: "loans",
        });
      }
    }

    // Lease expiry
    if (p.leaseEndDate) {
      const days = daysUntilDate(p.leaseEndDate);
      if (days <= 180) {
        items.push({
          id: `lease-expiry-${p.id}`,
          category: "lease-expiry",
          priority: days <= 60 ? "red" : "amber",
          title: "Lease expiring",
          description: `${name} lease ends ${formatDate(p.leaseEndDate)}`,
          daysUntil: days,
          dueDate: p.leaseEndDate,
          propertyId: p.id,
          propertyName: name,
          page: "simulator",
        });
      }
    }

    // Insurance renewal (gated by reminderEnabled per policy)
    for (const pol of p.insurancePolicies ?? []) {
      if (pol.reminderEnabled && pol.endDate) {
        const days = daysUntilDate(pol.endDate);
        if (days <= 60) {
          items.push({
            id: `insurance-renewal-${p.id}-${pol.id}`,
            category: "insurance-renewal",
            priority: days <= 14 ? "red" : "amber",
            title: `${pol.type || "Insurance"} renewal`,
            description: `${name} — ${pol.type || "policy"} expires ${formatDate(pol.endDate)}`,
            daysUntil: days,
            dueDate: pol.endDate,
            propertyId: p.id,
            propertyName: name,
            page: "portfolio",
          });
        }
      }
    }

    // Rent review
    if (p.rentReviewDate) {
      const days = daysUntilDate(p.rentReviewDate);
      if (days <= 90) items.push({ id: `rent-review-${p.id}`, category: "rent-review", priority: days <= 14 ? "red" : "amber", title: "Rent review due", description: `${name} — rent review ${formatDate(p.rentReviewDate)}`, daysUntil: days, dueDate: p.rentReviewDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Smoke alarm
    if (p.smokeAlarmExpiryDate) {
      const days = daysUntilDate(p.smokeAlarmExpiryDate);
      if (days <= 60) items.push({ id: `smoke-alarm-${p.id}`, category: "smoke-alarm", priority: days <= 14 ? "red" : "amber", title: "Smoke alarm cert expiring", description: `${name} — cert expires ${formatDate(p.smokeAlarmExpiryDate)}`, daysUntil: days, dueDate: p.smokeAlarmExpiryDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Pool safety
    if (p.poolSafetyCertDate) {
      const days = daysUntilDate(p.poolSafetyCertDate);
      if (days <= 60) items.push({ id: `pool-safety-${p.id}`, category: "pool-safety", priority: days <= 14 ? "red" : "amber", title: "Pool safety cert expiring", description: `${name} — cert expires ${formatDate(p.poolSafetyCertDate)}`, daysUntil: days, dueDate: p.poolSafetyCertDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Council rates
    if (p.councilRatesDueDate) {
      const days = daysUntilDate(p.councilRatesDueDate);
      if (days <= 30) items.push({ id: `council-rates-${p.id}`, category: "council-rates", priority: days <= 7 ? "red" : "amber", title: "Council rates due", description: `${name} — due ${formatDate(p.councilRatesDueDate)}`, daysUntil: days, dueDate: p.councilRatesDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Water rates
    if (p.waterRatesDueDate) {
      const days = daysUntilDate(p.waterRatesDueDate);
      if (days <= 30) items.push({ id: `water-rates-${p.id}`, category: "water-rates", priority: days <= 7 ? "red" : "amber", title: "Water rates due", description: `${name} — due ${formatDate(p.waterRatesDueDate)}`, daysUntil: days, dueDate: p.waterRatesDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Strata levy
    if (p.strataLevyDueDate) {
      const days = daysUntilDate(p.strataLevyDueDate);
      if (days <= 30) items.push({ id: `strata-levy-${p.id}`, category: "strata-levy", priority: days <= 7 ? "red" : "amber", title: "Strata levy due", description: `${name} — due ${formatDate(p.strataLevyDueDate)}`, daysUntil: days, dueDate: p.strataLevyDueDate, propertyId: p.id, propertyName: name, page: "portfolio" });
    }

    // Valuation stale
    if (p.lastValuationDate) {
      const daysSince = -daysUntilDate(p.lastValuationDate);
      if (daysSince > 730) items.push({ id: `valuation-stale-${p.id}`, category: "valuation-stale", priority: "amber", title: "Bank valuation older than 2 years", description: `${name} — last valued ${formatDate(p.lastValuationDate)}`, daysUntil: null, propertyId: p.id, propertyName: name, page: "portfolio" });
    }
  }

  // ── Portfolio-level alerts ──

  // LVR > 80%
  if (metrics.lvrPct > 80) {
    items.push({
      id: "lvr-high",
      category: "lvr-high",
      priority: "red",
      title: "LVR above 80%",
      description: `Portfolio LVR is ${metrics.lvrPct.toFixed(1)}% — pause acquisitions until equity improves`,
      page: "strategy",
    });
  }

  // Trust resolution deadline (31 Aug within 60 days)
  const aug31 = new Date(today.getFullYear(), 7, 31);
  const daysToAug31 = Math.round((aug31.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const hasTrustProps = active.some((p) => {
    const s = structures.find((st) => st.id === p.structureId);
    return s && isTrustStructure(s);
  });
  if (daysToAug31 >= 0 && daysToAug31 <= 60 && hasTrustProps) {
    items.push({
      id: "trust-deadline",
      category: "trust-deadline",
      priority: "amber",
      title: "Trust resolution deadline",
      description: `31 August trustee distribution deadline in ${daysToAug31} days`,
      daysUntil: daysToAug31,
      dueDate: aug31.toISOString().slice(0, 10),
      page: "strategy",
    });
  }

  // High monthly top-up
  if (metrics.monthlyShortfall > 3000) {
    items.push({
      id: "high-topup",
      category: "high-topup",
      priority: "amber",
      title: "High monthly top-up",
      description: `Portfolio requires $${Math.round(metrics.monthlyShortfall).toLocaleString()}/mo out of pocket`,
      page: "strategy",
    });
  }

  // Equity available
  if (metrics.usableEquity > 150000 && metrics.lvrPct < 75) {
    items.push({
      id: "equity-available",
      category: "equity-available",
      priority: "green",
      title: "Equity available",
      description: `~$${Math.round(metrics.usableEquity / 10000).toLocaleString()}0k usable at 80% LVR`,
      page: "strategy",
    });
  }

  // ── Filter disabled categories ──
  const filtered = items.filter((item) => !disabled.includes(item.category));

  // ── Sort: priority tier (red first), then daysUntil ASC ──
  filtered.sort((a, b) => {
    const pDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (pDiff !== 0) return pDiff;
    const aDays = a.daysUntil ?? Infinity;
    const bDays = b.daysUntil ?? Infinity;
    return aDays - bDays;
  });

  return enrichEmailActions(filtered);
}
